import {isFunction, isObject} from 'lodash';
import {FC, useCallback, useEffect, useState} from 'react';

import {isInteger} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {HelpChatConfigModel} from '../../../models/api/helpChatConfigModel';

/*
 * Props.
 */

interface HelpChatProps {
  user: TeammateModel;
  helpChatConfig: HelpChatConfigModel;
  helpChatOpenAttempt: number;
}

/*
 * Constants.
 */

const chatbotMessageUserSource = 'user';

/*
 * Component.
 */

export const HelpChat: FC<HelpChatProps> = (props) => {
  const {user, helpChatConfig, helpChatOpenAttempt} = props;
  const [chatWidget, setChatWidget] = useState<(...args: Array<any>) => void>();
  const {window} = useLayer();

  /*
   * Manage the widget's lifecycle.
   */

  const onScriptLoad = useCallback(() => {
    // The "FrontChat" function changes over time.
    // As a result, we need to call it on the window every time.
    const newChatWidget = (...args: Array<any>): unknown => {
      if (!('FrontChat' in window) || !isFunction(window.FrontChat)) {
        return undefined;
      }

      // Do not allow exceptions from the Front Chat SDK to affect the client.
      try {
        return window.FrontChat(...args);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return undefined;
      }
    };

    setChatWidget(() => newChatWidget);
  }, [window]);

  useEffect(() => {
    const scriptDocument = window.document;
    const script = scriptDocument.createElement('script');
    script.type = 'text/javascript';
    script.src = helpChatConfig.bundleSrc || 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    script.async = true;
    script.onload = onScriptLoad;

    scriptDocument.body.appendChild(script);
    return () => {
      scriptDocument.body.removeChild(script);
    };
  }, [window, onScriptLoad, helpChatConfig.bundleSrc]);

  /*
   * Control the widget.
   */

  const {chatId, userHash, namespace} = helpChatConfig;
  const userEmail = user.email;
  const userDisplayName = buildTeammateDisplayName(user);

  const openHelpChat = useCallback(() => {
    if (!chatWidget) {
      return;
    }

    chatWidget('show');
  }, [chatWidget]);

  // Update the widget configuration when it changes.
  useEffect(() => {
    if (!chatWidget) {
      return undefined;
    }

    const onUnreadChange = (options: unknown) => {
      if (
        !isObject(options) ||
        !('unread_count' in options) ||
        !isInteger(options.unread_count) ||
        !('source' in options)
      ) {
        return;
      }

      // Unread messages can also arrive from the chatbot. Do not show the chat window for this.
      if (options.unread_count === 0 || options.source !== chatbotMessageUserSource) {
        return;
      }

      openHelpChat();
    };

    // Wait for the widget to fully initialize before binding event handlers.
    const onInitCompleted = () => {
      chatWidget('onUnreadChange', onUnreadChange);
    };

    chatWidget('init', {
      useDefaultLauncher: false,
      chatId,
      userHash,
      email: userEmail,
      name: userDisplayName,
      namespace,
      onInitCompleted,
    });

    return () => {
      chatWidget('offUnreadChange', onUnreadChange);
      chatWidget('shutdown');
    };
  }, [chatWidget, chatId, userHash, userEmail, userDisplayName, namespace, openHelpChat]);

  // Open the widget when requested.
  useEffect(() => {
    if (helpChatOpenAttempt === 0) {
      return;
    }

    openHelpChat();
  }, [helpChatOpenAttempt, openHelpChat]);

  /*
   * Render.
   */

  return null;
};
